/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Circle, Clock, ArrowRight } from "lucide-react";
import apiService from "@/services/apiService";
import useErrorStore from "@/store/confirmationStore";
import { getUserInfo } from "@/services/userService";

interface StageUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  currentStages: string[];
  onSuccess?: () => void;
}

const CASE_STAGES = [
  { key: "SHO_REPORT", label: "SHO Report", description: "SHO submitted the report" },
  { key: "CO_SIGN", label: "CO Approval", description: "Circle Officer approval" },
  { key: "SDM_SIGN", label: "SDM Approval", description: "Sub Divisional Magistrate approval" },
  { key: "SP_PERMISSION", label: "SP Permission", description: "Superintendent of Police permission" },
  { key: "GD_ENTRY", label: "GD Entry", description: "GD entry in police station" },
  { key: "DM_NOTICE_ISSUED", label: "DM Notice", description: "District Magistrate Court issued notice" },
  { key: "UNDER_HEARING", label: "Under Hearing", description: "Case under hearing" },
  { key: "FINAL_ORDER", label: "Final Order", description: "Final judgment / order issued" },
];

export function StageUpdateDialog({ 
  open, 
  onOpenChange, 
  caseId, 
  currentStages,
  onSuccess 
}: StageUpdateDialogProps) {
  const showError = useErrorStore((s) => s.showError);
  const [selectedStage, setSelectedStage] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get available stages (not yet completed)
  const getAvailableStages = () => {
    return CASE_STAGES.filter(stage => !currentStages.includes(stage.key));
  };

  // Get next recommended stage
  const getNextStage = () => {
    const availableStages = getAvailableStages();
    return availableStages.length > 0 ? availableStages[0] : null;
  };

  const handleSubmit = async () => {
    if (!selectedStage) {
      showError("Please select a stage");
      return;
    }

    setIsSubmitting(true);
    try {
      const userInfo: any = getUserInfo();
      
      const response = await apiService.post(
        `goonda-act-case/${caseId}/progress`,
        JSON.stringify({
          stage: selectedStage,
          remarks: remarks || undefined,
          performedBy: userInfo?.name,
          designation: userInfo?.designation || userInfo?.role,
        })
      );

      if (response) {
        toast.success("Case progress updated successfully!", {
          description: `Stage updated to ${CASE_STAGES.find(s => s.key === selectedStage)?.label}`,
        });
        setSelectedStage("");
        setRemarks("");
        onOpenChange(false);
        if (onSuccess) onSuccess();
      }
    } catch (error: any) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStage = getNextStage();
  const availableStages = getAvailableStages();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-black">Update Case Progress</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Progress Overview */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-sm text-gray-700 mb-3">Current Progress</h3>
            <div className="space-y-2">
              {CASE_STAGES.map((stage) => {
                const isCompleted = currentStages.includes(stage.key);
                const isNext = nextStage?.key === stage.key;
                
                return (
                  <div key={stage.key} className="flex items-center gap-3">
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : isNext ? (
                      <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    ) : (
                      <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <span className={`text-sm ${
                        isCompleted ? "text-green-700 font-medium" : 
                        isNext ? "text-blue-700 font-medium" : 
                        "text-gray-500"
                      }`}>
                        {stage.label}
                      </span>
                    </div>
                    {isNext && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        Next Stage
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {availableStages.length === 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-2" />
              <p className="text-green-800 font-semibold">All stages completed!</p>
              <p className="text-sm text-green-700 mt-1">This case has reached its final stage.</p>
            </div>
          ) : (
            <>
              {/* Stage Selection */}
              <div className="space-y-2">
                <Label htmlFor="stage" className="text-sm font-semibold">
                  Select Stage to Update *
                </Label>
                {nextStage && (
                  <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
                    <ArrowRight className="w-4 h-4" />
                    <span>Recommended: {nextStage.label}</span>
                  </div>
                )}
                <Select value={selectedStage} onValueChange={setSelectedStage}>
                  <SelectTrigger id="stage" className="w-full">
                    <SelectValue placeholder="Choose a stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStages.map((stage) => (
                      <SelectItem key={stage.key} value={stage.key}>
                        <div className="flex flex-col">
                          <span className="font-medium">{stage.label}</span>
                          <span className="text-xs text-gray-500">{stage.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label htmlFor="remarks" className="text-sm font-semibold">
                  Remarks (Optional)
                </Label>
                <Textarea
                  id="remarks"
                  placeholder="Enter any additional notes or comments about this stage update..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={4}
                  className="resize-none"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {availableStages.length > 0 && (
            <Button 
              onClick={handleSubmit}
              disabled={!selectedStage || isSubmitting}
              className="bg-[#0d2846] text-white hover:bg-[#0d2846]/90"
            >
              {isSubmitting ? "Updating..." : "Update Progress"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}